"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

/** The help desk, built to match the KindnessBot widget on Kindness Matrix so
 *  the two read as one product: a named pill in the corner, a panel with an
 *  avatar and title, suggested openers, a rounded input with a round send
 *  button, and one line of small print underneath.
 *
 *  COPY FINAL 2026-09-07. The closing line is the owner's wording, verbatim.
 *
 *  It talks to /api/kindnessbot, never to the bot itself: the bot's token is a
 *  server secret, and a browser holding it would be the same as having none. */

interface Turn {
  role: "user" | "assistant";
  content: string;
}

/** Matches the route and the bot. Sending more would be silently truncated
 *  upstream, and a reply that ignored half the conversation reads as the bot
 *  forgetting rather than as a limit. */
const MAX_HISTORY = 20;

/** Shown until the first message, then gone. Deliberately questions this help
 *  desk answers well — a suggestion the bot then fumbles is worse than no
 *  suggestion, because we chose it. */
const SUGGESTIONS = [
  "What can I do with KindnessGuild?",
  "How do I find a guild near me?",
  "I want to start a guild.",
  "What is a guild?",
];

/** Juju, the KindnessBot logo.
 *
 *  The source art is a full circular badge — the dog above a "juju ai" wordmark
 *  — and at 22px that wordmark is unreadable noise. The CSS crops to the face
 *  instead, so the avatar reads as a face rather than as a smudge, while the
 *  asset stays the logo exactly as supplied.
 *
 *  alt="" and aria-hidden: it sits beside the word "KindnessBot" every time it
 *  appears, so naming it again would make a screen reader say it twice. */
function BotMark() {
  return (
    <span className="g-help-mark" aria-hidden="true">
      <Image
        src="/assets/kindnessbot.jpg"
        alt=""
        width={44}
        height={44}
        className="g-help-mark__img"
      />
    </span>
  );
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M20 12H7m0 0 5-5m-5 5 5 5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

export function HelpDesk() {
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Escape closes, from anywhere. A dialog you cannot dismiss with the keyboard
  // is a dialog somebody is trapped in.
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Keep the newest message in view. Not smooth-scrolled: this fires while
  // somebody is reading, and animating the ground under them is worse than
  // moving it.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [turns, busy]);

  async function send(text?: string) {
    const message = (text ?? draft).trim();
    if (message === "" || busy) return;

    const history = turns.slice(-MAX_HISTORY);
    setTurns((t) => [...t, { role: "user", content: message }]);
    setDraft("");
    setBusy(true);
    setError(null);

    try {
      const res = await fetch("/api/kindnessbot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, history }),
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        // The route's messages are already written for a person — the rate
        // limit one in particular says what to do — so they are shown rather
        // than replaced with something vaguer.
        setError(data?.error ?? "The help desk is unavailable just now.");
        return;
      }
      setTurns((t) => [...t, { role: "assistant", content: data.reply }]);
    } catch {
      setError("Could not reach the help desk. Please check your connection.");
    } finally {
      setBusy(false);
    }
  }

  const started = turns.length > 0 || busy;

  // Reopening the widget shows the same conversation, since it stays mounted
  // for the life of the page rather than resetting on close -- deliberate, so
  // a reply isn't lost just because the panel was dismissed for a moment. This
  // is the way back to a clean start when that's what's wanted instead.
  // Disabled while busy: a reply already in flight would otherwise land on the
  // conversation that replaced the one it was answering.
  function startOver() {
    setTurns([]);
    setDraft("");
    setError(null);
    inputRef.current?.focus();
  }

  return (
    <>
      {/* Named, not a bare "?": somebody has to be able to tell what it is
          before deciding to open it. Hidden while the panel is open, because
          the panel has its own close and two would be one too many. */}
      {!open && (
        <button
          type="button"
          className="g-help-fab"
          onClick={() => setOpen(true)}
          aria-expanded={false}
          aria-controls="kindnessbot-panel"
        >
          <BotMark />
          KindnessBot
        </button>
      )}

      {open && (
        <dialog open id="kindnessbot-panel" className="g-help-panel" aria-label="KindnessBot">
          <div className="g-help-head">
            <button
              type="button"
              className="g-help-reset"
              onClick={startOver}
              disabled={!started || busy}
              aria-label="Start a new conversation"
            >
              <BackIcon />
            </button>
            <BotMark />
            <span className="g-help-title">KindnessBot</span>
            <button
              type="button"
              className="g-help-close"
              onClick={() => setOpen(false)}
              aria-label="Close KindnessBot"
            >
              ×
            </button>
          </div>

          <div className="g-help-log" ref={logRef}>
            {!started && (
              <>
                <p className="g-help-intro">Ask me about KindnessGuild. Try:</p>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="g-help-suggestion"
                    onClick={() => void send(s)}
                  >
                    {s}
                  </button>
                ))}
              </>
            )}

            {turns.map((turn, i) => (
              <p
                key={i}
                className={`g-help-msg ${turn.role === "user" ? "g-help-msg--you" : "g-help-msg--bot"}`}
              >
                {turn.content}
              </p>
            ))}

            {busy && (
              <p className="g-help-msg g-help-msg--bot g-help-msg--waiting" aria-live="polite">
                Thinking…
              </p>
            )}

            {error && (
              <p role="alert" className="g-help-msg g-help-msg--error">
                {error}
              </p>
            )}
          </div>

          {/* A one-line input, like the widget this matches, so Enter submits
              the form rather than needing a key handler of its own. */}
          <form
            className="g-help-compose"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <input
              ref={inputRef}
              type="text"
              className="g-help-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ask about KindnessGuild..."
              maxLength={8000}
              aria-label="Your message"
            />
            <button
              type="submit"
              className="g-help-send"
              disabled={busy || draft.trim() === ""}
              aria-label="Send"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path
                  d="M4 12h13m0 0-5-5m5 5-5 5"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </button>
          </form>

          {/* Owner's wording, 2026-09-07. Verbatim. */}
          <p className="g-help-foot">
            Kindness Bot is AI and can make mistakes. Always use your best judgment, be safe, and
            have fun!
          </p>
        </dialog>
      )}
    </>
  );
}
